import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FinanceKeys } from "web_myplann/Api/Finances/financeKeys";
import {
	type ClientExpenseListItemResponseDTO,
	type ClientFinancialPlanUpdateRequestDTO,
	type ClientIncomeDetailResponseDTO,
	type FinancialPlanUpdateResponseDTO,
	patchClientFinancialPlanV2,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";

type ExpensesQueryData = {
	data: ClientExpenseListItemResponseDTO[];
	meta: { totalCount: number };
};

type IncomesQueryData = {
	data: ClientIncomeDetailResponseDTO[];
};

export const usePatchFinanceMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: processResponse(
			(requestContract: ClientFinancialPlanUpdateRequestDTO) =>
				patchClientFinancialPlanV2(requestContract),
		),
		onMutate: async (requestContract: ClientFinancialPlanUpdateRequestDTO) => {
			await queryClient.cancelQueries({ queryKey: FinanceKeys.expense() });
			await queryClient.cancelQueries({ queryKey: FinanceKeys.income() });

			const previousExpenses = queryClient.getQueryData<ExpensesQueryData>(
				FinanceKeys.expense(),
			);
			const previousIncomes = queryClient.getQueryData<IncomesQueryData>(
				FinanceKeys.income(),
			);

			if (requestContract.expenses && previousExpenses) {
				const updatedExpenses = previousExpenses.data.map((expense) => {
					const update = requestContract.expenses?.find(
						(e) => e.id != null && e.id === expense.id,
					);
					return update
						? {
								...expense,
								name: update.name,
								type: update.type,
								monthlyPayment: update.monthlyPayment,
								sustainableAmount: update.sustainableAmount,
							}
						: expense;
				});

				queryClient.setQueryData<ExpensesQueryData>(FinanceKeys.expense(), {
					...previousExpenses,
					data: updatedExpenses,
				});
			}

			if (requestContract.incomes && previousIncomes) {
				const updatedIncomes = previousIncomes.data.map((income) => {
					const update = requestContract.incomes?.find(
						(i) => i.id != null && i.id === income.id,
					);
					return update
						? {
								...income,
								name: update.name,
								amount: update.amount,
							}
						: income;
				});

				queryClient.setQueryData<IncomesQueryData>(FinanceKeys.income(), {
					...previousIncomes,
					data: updatedIncomes,
				});
			}

			return { previousExpenses, previousIncomes };
		},
		onError: (_error, _variables, context) => {
			if (context?.previousExpenses) {
				queryClient.setQueryData(
					FinanceKeys.expense(),
					context.previousExpenses,
				);
			}
			if (context?.previousIncomes) {
				queryClient.setQueryData(FinanceKeys.income(), context.previousIncomes);
			}
		},
		onSuccess: (response: FinancialPlanUpdateResponseDTO | undefined) => {
			if (!response) {
				return;
			}
			queryClient.setQueryData<IncomesQueryData>(FinanceKeys.income(), {
				data: response.incomes,
			});
			queryClient.setQueryData<ExpensesQueryData>(
				FinanceKeys.expense(),
				(prev) => ({
					meta: prev?.meta ?? { totalCount: response.expenses.length },
					data: response.expenses,
				}),
			);
		},
	});
};
